import { useEffect } from "react";

import type { ReviewFinished } from "@/lib/review";

import { EnterKey } from "./EnterKey";
import { elapsed } from "./sessionRules";

/**
 * `7e`. What a sitting ends on.
 *
 * **The session never dissolves on its own.** Finishing lands here, inside the session frame,
 * rather than bouncing back to the landing — a silent bounce reads as a crash, and it throws away
 * the only moment where the day's numbers are worth showing.
 *
 * **The two states are the same screen.** Same order, same sizes: headline, four counters, streak.
 * The only real difference is what you can do next — start the deck that still has cards, or stop —
 * so nothing is reordered or resized to dramatise which ending you got.
 */

interface Props {
  finished: ReviewFinished;
  isPhone: boolean;
  canUndo: boolean;
  onStudyNext: (deck: string) => void;
  onUndo: () => void;
  onDone: () => void;
  onOpenStats: () => void;
}

export function Finished({
  finished,
  isPhone,
  canUndo,
  onStudyNext,
  onUndo,
  onDone,
  onOpenStats,
}: Props) {
  const more = finished.nextDeck !== null;

  /**
   * The three keys this screen draws.
   *
   * They were printed and did nothing: the only keydown listener in Review lives in `Session`,
   * which is not rendered here — `7e` replaces the card, not the chrome. A hint for a key that does
   * nothing is worse than no hint, because it is the one you reach for after twenty minutes of
   * `←`/`→`/`↑` doing exactly what they said.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;

      if (event.key === "Escape") {
        event.preventDefault();
        onDone();
      } else if (event.key === "Enter" && finished.nextDeck) {
        event.preventDefault();
        onStudyNext(finished.nextDeck);
      } else if (event.key === "ArrowUp" && canUndo) {
        event.preventDefault();
        onUndo();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canUndo, finished.nextDeck, onDone, onStudyNext, onUndo]);

  const rate =
    finished.reviewed === 0 ? null : Math.round((100 * finished.passed) / finished.reviewed);

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-[18px] md:p-10">
      <div className="flex w-full max-w-[660px] flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-eyebrow font-semibold uppercase tracking-[.12em] text-ink">
            {more ? "デッキ完了" : "すべて完了"}
          </span>
          <h3
            className="font-semibold tracking-[-.01em] text-ink"
            style={{ fontSize: isPhone ? 25 : 34, lineHeight: 1.15 }}
          >
            {more ? `${finished.deck} は今日はおしまい。` : "今日の復習はすべて終わりました。"}
          </h3>
          <p className="text-body text-ink-2" style={{ textWrap: "pretty" }}>
            {more
              ? `${finished.nextDeck} は要復習${finished.nextDeckDue}枚。`
              : `${finished.reviewed}枚を復習。明日は${finished.dueTomorrow}枚が期限です。`}
          </p>
        </div>

        <div className="flex gap-8 rounded-[var(--r-4)] border border-line-strong bg-panel px-5 py-[18px] shadow-[var(--sh-frame)]">
          <Counter value={finished.reviewed.toLocaleString()} label="復習数" isPhone={isPhone} />
          {/* Never `0%` for a sitting with no answers — that is a claim of total failure where the
              honest answer is that there is nothing to rate yet. Same rule the Stats tabs keep. */}
          <Counter value={rate === null ? "—" : `${rate}%`} label="正解率" isPhone={isPhone} />
          <Counter value={elapsed(finished.elapsedSeconds)} label="経過" isPhone={isPhone} />
          {!isPhone && (
            <Counter value={String(finished.newSeen)} label="新規学習" isPhone={isPhone} />
          )}
        </div>

        <div className="flex flex-col gap-3">
          {more ? (
            <button
              type="button"
              onClick={() => onStudyNext(finished.nextDeck!)}
              data-check="review.start-next"
              className="flex h-16 items-center justify-center gap-3 rounded-[var(--r-4)] bg-ink text-void"
            >
              <span className="text-[20px]/[1] font-semibold">{finished.nextDeck}を復習</span>
              <span className="text-body text-ink-off">要復習{finished.nextDeckDue}</span>
              {/* **Drawn, not typed, and NOT in `currentColor`** (日本-501). It was `↵` (U+21B5) in
                  `text-ink-3` on this bone `bg-ink` button — measured at **1.71:1**, the same class
                  of bug 日本-500 fixed on the landing: a token used against the wrong ground. It
                  also carried that ticket's other two faults, which are properties of the glyph
                  rather than of the colour, and are why `EnterKey` exists.

                  **The colour is a decision, not a copy-paste.** On the landing the mark inherits
                  `text-white` on an accent fill, so it is exactly as loud as the label. Here the
                  label is `text-void`, and a `currentColor` arrow would be 15.28:1 — a second
                  label. This button has a three-part hierarchy the landing does not (label,
                  `要復習{N}`, key), and both of its siblings — that count, and the `Esc` on the 完了
                  button below — are already `--ink-off` at 8.41:1. So the key takes the hint's ink
                  and reads as a hint.

                  **`-0.7px` is calibrated for THIS label and is not the landing's number.** That
                  one was judged against 19px CJK; this label is 20px, and it sits beside a
                  `要復習{N}` the landing does not have. Re-judged on a rendered ladder by eye, which
                  is 日本-500's method — a centroid and a bounding-box formula were both tried there
                  and both failed against Alex's eye, so **do not replace this with either.**

                  The ladder ran 0 / −0.4 / −0.6 / −0.8 / −1.0 / −1.3 at 3× against the real
                  colours: 0 and −0.4 read low, −1.0 and −1.3 read high, and **−0.6 through −0.8 are
                  indistinguishable.** So this is the centre of a band rather than a knife edge —
                  worth knowing before anyone re-tunes it by a tenth and believes the tenth. */}
              <EnterKey check="review.next.key" offsetPx={-0.7} className="text-ink-off" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onDone}
              data-check="review.done"
              className="flex h-16 items-center justify-center gap-3 rounded-[var(--r-4)] border border-line-strong bg-panel text-ink"
            >
              <span className="text-[20px]/[1] font-semibold">完了</span>
              <span className="font-mono text-secondary text-ink-off">Esc</span>
            </button>
          )}

          <div className="flex items-center gap-3.5">
            {/* Undo survives into BOTH endings, because the commonest reason to want it is
                realising you misgraded the card that ended the session. */}
            <button
              type="button"
              disabled={!canUndo}
              onClick={onUndo}
              data-check="review.undo"
              className="flex items-center gap-2 rounded-[var(--r-2)] border border-line-strong bg-panel px-4 py-2.5 text-secondary font-semibold text-ink-2 disabled:opacity-40"
            >
              直前を取り消し
              <span className="font-mono text-meta text-ink-off">↑</span>
            </button>
            {more && (
              <button
                type="button"
                onClick={onDone}
                data-check="review.done"
                className="flex items-center gap-2 rounded-[var(--r-2)] border border-line-strong bg-panel px-4 py-2.5 text-secondary font-semibold text-ink-2"
              >
                復習へ戻る
                <span className="font-mono text-meta text-ink-off">Esc</span>
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onOpenStats}
              className="text-body text-[color:var(--accent-link)] hover:underline"
            >
              統計 ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Counter({
  value,
  label,
  isPhone,
}: {
  value: string;
  label: string;
  isPhone: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {/* Sans-serif 600, never mono: mono is for COLUMNS of figures, where the point is that the
          digits align. These are four headline numbers in a row. */}
      <span
        className="font-semibold tracking-[-.02em] text-ink"
        style={{ fontSize: isPhone ? 24 : 30, lineHeight: 1 }}
      >
        {value}
      </span>
      <span className="text-eyebrow uppercase tracking-[.08em] text-ink-3">{label}</span>
    </div>
  );
}
